const RETRY_KEY = "aruza.module-recovery.last-reload.v1";
const RETRY_COOLDOWN_MS = 5 * 60 * 1000;
const preloadErrors = new WeakSet<object>();

// Vite also reports stylesheet preload errors. Keep the original rejection so
// React Router and monitoring still receive it; do not preventDefault().
export function recordPreloadError(error: unknown): void {
    if (typeof error === "object" && error !== null) preloadErrors.add(error);
}

export function isModuleLoadError(error: unknown): boolean {
    if (typeof error !== "object" || error === null) return false;
    if (preloadErrors.has(error)) return true;
    return (
        error instanceof Error &&
        /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|not a valid JavaScript MIME type for module script|Unable to preload CSS/i.test(
            error.message
        )
    );
}

export type RetryResult = "reloading" | "offline" | "limited" | "storage-unavailable";

// A user-requested full reload also clears React.lazy's rejected promise and
// browsers' failed module maps. Never change the URL or clear login/MFA data.
// The single tab-wide guard survives reloads AND changes of app version/route.
export function retryModuleLoad(
    online: boolean,
    storage: Pick<Storage, "getItem" | "setItem">,
    reload: () => void,
    now = Date.now()
): RetryResult {
    if (!online) return "offline";
    try {
        const previous = storage.getItem(RETRY_KEY);
        if (previous !== null) {
            const timestamp = Number(previous);
            if (!Number.isFinite(timestamp) || now - timestamp < RETRY_COOLDOWN_MS) {
                return "limited";
            }
        }
        storage.setItem(RETRY_KEY, String(now));
    } catch {
        // Fail closed when a persistent guard cannot be saved.
        return "storage-unavailable";
    }
    reload();
    return "reloading";
}
